// SEO utility functions for managing Schema.org JSON-LD

interface OrganizationSchema {
  '@context': string;
  '@type': string;
  name: string;
  url: string;
  logo?: string;
  foundingDate?: string;
  description?: string;
  address?: {
    '@type': string;
    addressCountry?: string;
  };
  contactPoint?: {
    '@type': string;
    contactType: string;
    email?: string;
  };
}

interface WebSiteSchema {
  '@context': string;
  '@type': string;
  name: string;
  url: string;
  description?: string;
  publisher?: {
    '@type': string;
    name: string;
  };
}

interface WebPageSchema {
  '@context': string;
  '@type': string;
  name: string;
  description: string;
  url: string;
  publisher: {
    '@type': string;
    name: string;
  };
}

interface ProductSchema {
  '@context': string;
  '@type': string;
  name: string;
  description: string;
  brand: {
    '@type': string;
    name: string;
  };
  offers?: {
    '@type': string;
    availability: string;
  };
}

export const injectSchemaOrg = (): void => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  // Remove any existing schema scripts first
  const existingSchemas = document.querySelectorAll('script[type="application/ld+json"]');
  existingSchemas.forEach((schema) => schema.remove());

  // Organization Schema
  const organizationSchema: OrganizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'HANBUM Aircraft Shelter Solutions',
    url: siteUrl,
    foundingDate: '1976',
    description: 'Professional hardened aircraft shelter arch panels for military and aviation facilities since 1976.',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
    },
  };

  // WebSite Schema
  const websiteSchema: WebSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'HANBUM Aircraft Shelter Solutions',
    url: siteUrl,
    description: 'Professional hardened aircraft shelter arch panels for military and aviation facilities.',
    publisher: {
      '@type': 'Organization',
      name: 'HANBUM Aircraft Shelter Solutions',
    },
  };

  // WebPage Schema
  const webpageSchema: WebPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Aircraft Shelter Arch Panels | Hardened Military Aviation Infrastructure',
    description: 'HANBUM EST. 1976 specializes in professional hardened aircraft shelter arch panels for military and aviation facilities.',
    url: siteUrl,
    publisher: {
      '@type': 'Organization',
      name: 'HANBUM Aircraft Shelter Solutions',
    },
  };

  // Product Schema for main offering
  const productSchema: ProductSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Hardened Aircraft Shelter Arch Panels',
    description: 'Professional arch panel systems for military aircraft shelters and hangars. Durable, weather-resistant construction solutions engineered for maximum protection.',
    brand: {
      '@type': 'Brand',
      name: 'HANBUM',
    },
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
    },
  };

  // Inject all schemas
  const schemas = [organizationSchema, websiteSchema, webpageSchema, productSchema];
  
  schemas.forEach((schema) => {
    const scriptElement = document.createElement('script');
    scriptElement.type = 'application/ld+json';
    scriptElement.textContent = JSON.stringify(schema);
    document.head.appendChild(scriptElement);
  });
};

// Update page meta tags dynamically
export const updatePageMeta = (config: {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
}): void => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  if (config.title) {
    document.title = config.title;
    
    // Update Open Graph
    let ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement;
    if (ogTitle) ogTitle.content = config.title;
    
    // Update Twitter
    let twitterTitle = document.querySelector('meta[property="twitter:title"]') as HTMLMetaElement;
    if (twitterTitle) twitterTitle.content = config.title;
  }

  if (config.description) {
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (metaDesc) metaDesc.content = config.description;
    
    // Update Open Graph
    let ogDesc = document.querySelector('meta[property="og:description"]') as HTMLMetaElement;
    if (ogDesc) ogDesc.content = config.description;
    
    // Update Twitter
    let twitterDesc = document.querySelector('meta[property="twitter:description"]') as HTMLMetaElement;
    if (twitterDesc) twitterDesc.content = config.description;
  }

  if (config.keywords) {
    let metaKeywords = document.querySelector('meta[name="keywords"]') as HTMLMetaElement;
    if (metaKeywords) metaKeywords.content = config.keywords;
  }

  if (config.canonical) {
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonicalLink) {
      canonicalLink.href = `${siteUrl}${config.canonical}`;
    }
  }

  // Update last-modified date
  const lastModified = document.querySelector('meta[name="last-modified"]') as HTMLMetaElement;
  if (lastModified) {
    lastModified.content = new Date().toISOString().split('T')[0];
  }
};
